import { ChevronRightIcon } from "lucide-react"
import type { ReactNode } from "react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { HelpTooltip } from "./help-tooltip.tsx"
import { Toggletip } from "./toggletip.tsx"
import {
  meterOf,
  percentOf,
  progressFigureOf,
  progressInWords,
  progressPartsOf,
  type TicketCounts,
} from "./stat.ts"

/*
 * A figure worth watching, and how far along its budget it is. The page formats every figure: a
 * StatTile and a Meter only lay them out, so money keeps its one wording in `spend/describe.ts`.
 */

// The sub line is 12px text on a card, so its tones are the text-strength ones: plain success sits
// right on 4.5:1 there.
const subTones = {
  neutral: "text-muted-foreground",
  success: "text-success-text",
  warning: "text-warning-text",
} as const

/**
 * The row of StatTiles across the top of a dashboard: four columns on the desk, two on `compact`,
 * where a `span` 2 tile takes a row of its own and the next two share one, so a phone's figures
 * still fit their tiles.
 */
export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-4 gap-3 compact:grid-cols-2">{children}</div>
}

/**
 * One figure on a raised tile: a label, with its definition in a tooltip when `help` is set; the
 * figure itself, large and tabular; and an optional line under it saying what the figure means,
 * in the tone of that meaning. `children` go under all three, such as a Meter. `span` 2 takes two
 * columns of the page's grid. `quiet` sets the figure in the subtle ink, for a figure that says
 * nothing needs watching, such as "None".
 *
 * With `onOpen` the whole tile is a button that opens what the figure is made of (an Insights
 * figure's tickets), its label, figure and line naming it; a chevron at its corner shows on hover
 * and focus. Such a tile has no `help`, since a tooltip can't sit inside a button.
 */
export function StatTile({
  label,
  help,
  value,
  sub,
  span = 1,
  quiet = false,
  onOpen,
  children,
}: {
  label: string
  value: string
  sub?: { text: string; tone: keyof typeof subTones }
  span?: 1 | 2
  quiet?: boolean
  children?: ReactNode
} & ({ help?: string; onOpen?: never } | { help?: never; onOpen: () => void })) {
  const tile = cn(
    "block rounded-lg bg-card p-4 text-left text-card-foreground shadow-raised ring-1 ring-border",
    span === 2 && "col-span-2",
  )
  const content = (
    <>
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {help && <HelpTooltip about={label}>{help}</HelpTooltip>}
      </span>
      <span
        className={cn(
          "mt-1 block text-stat font-semibold tabular-nums",
          quiet && "text-subtle-foreground",
        )}
      >
        {value}
      </span>
      {sub && <span className={cn("mt-1 block text-xs", subTones[sub.tone])}>{sub.text}</span>}
      {children}
    </>
  )
  if (!onOpen) return <div className={tile}>{content}</div>
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        tile,
        "group/tile relative w-full cursor-pointer transition-colors duration-100 ease-snappy outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      {content}
      <ChevronRightIcon
        aria-hidden
        className="absolute top-3.5 right-3 size-4 text-subtle-foreground opacity-0 transition-opacity duration-100 group-hover/tile:opacity-100 group-focus-visible/tile:opacity-100 touch:opacity-100"
      />
    </button>
  )
}

/**
 * How much of a budget is spent, on a thin track the width of the budget: the spent part fills it,
 * amber once it reaches the budget; the `projected` part hatches on after it; and `ticks` mark
 * amounts along the way, each explained in a tooltip. Every amount is in the budget's units. On
 * touch, where nothing hovers, `caption` says what the ticks mark, as a line under the track.
 */
export function Meter({
  spent,
  projected,
  budget,
  ticks = [],
  caption,
}: {
  spent: number
  projected?: number
  budget: number
  ticks?: readonly { at: number; tooltip: string }[]
  /** What the ticks mark, in a line, for touch. */
  caption?: string
}) {
  const meter = meterOf({ spent, ...(projected !== undefined && { projected }), budget })
  const spentPercent = Math.round(percentOf(spent, budget))
  return (
    <>
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={budget}
        aria-valuenow={Math.min(spent, budget)}
        aria-valuetext={`${spentPercent}% of the budget`}
        className="relative h-1.5 rounded-full bg-meter-track"
      >
        {/* beacon: the widths and positions below are the data being drawn, not design values,
          so they are the one inline style a composite may set. */}
        <div className="flex h-full overflow-hidden rounded-full">
          <div
            className={cn("h-full", meter.reached ? "bg-warning" : "bg-primary")}
            style={{ width: `${meter.spent}%` }}
          />
          {meter.projected > 0 && (
            <div className="meter-projected h-full" style={{ width: `${meter.projected}%` }} />
          )}
        </div>
        {ticks.map(({ at, tooltip }) => (
          <Tooltip key={at}>
            <TooltipTrigger
              render={<span />}
              aria-label={tooltip}
              className="absolute -top-0.75 h-3 w-px -translate-x-1/2 bg-border-strong"
              style={{ left: `${percentOf(at, budget)}%` }}
            />
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        ))}
      </div>
      {caption && (
        <p className="mt-1.5 hidden text-xs text-subtle-foreground touch:block">{caption}</p>
      )}
    </>
  )
}

const progressWidths = { 10: "w-10", 20: "w-20", 24: "w-24", full: "w-full" } as const

/**
 * How far along something is, as a short thin bar beside its figure, such as a rate, in the muted
 * ink, or (`full`) across its column under the words saying how far a task has come, as a bulk
 * download's does. A native progress bar, so how full it is stays a value rather than a style. `label` names
 * it for a screen reader; without one it is hidden, for the figure beside it already says it.
 */
export function ProgressBar({
  value,
  max,
  width,
  label,
}: {
  value: number
  max: number
  width: keyof typeof progressWidths
  label?: string
}) {
  return (
    <progress
      value={value}
      max={max}
      {...(label ? { "aria-label": label } : { "aria-hidden": true })}
      className={cn(
        "h-1 shrink-0 appearance-none overflow-hidden rounded-full bg-meter-track [&::-moz-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-meter-track [&::-webkit-progress-value]:rounded-full",
        "[&::-moz-progress-bar]:bg-muted-foreground [&::-webkit-progress-value]:bg-muted-foreground",
        progressWidths[width],
      )}
    />
  )
}

// Each part's fill in its Status category's colour, as the Board's circles have it.
const partTones = {
  done: "bg-status-done",
  started: "bg-status-started",
} as const

/**
 * How a set of tickets stands, such as an Epic's: a short track filled from the left with its Done
 * and Started tickets, each in its category's colour, the rest of the track those not started, and
 * beside it how many are done of how many there are. Cancelled tickets are left out of both. The figure keeps one width
 * whatever it says, so bars down a list line up. Its Toggletip, and its name to a screen reader, say
 * each count in words.
 */
export function TicketProgress({
  counts,
  width,
}: {
  counts: TicketCounts
  width: keyof typeof progressWidths
}) {
  const said = progressInWords(counts)
  return (
    <span className="flex shrink-0 items-center gap-2">
      <Toggletip tip={said}>
        <span
          role="img"
          aria-label={said}
          className={cn(
            "flex h-1.5 shrink-0 overflow-hidden rounded-full bg-meter-track",
            progressWidths[width],
          )}
        >
          {progressPartsOf(counts).map(({ category, percent }) => (
            // beacon: each part's width is the data being drawn, not a design value.
            <span key={category} className={partTones[category]} style={{ width: `${percent}%` }} />
          ))}
        </span>
      </Toggletip>
      <span aria-hidden className="w-12 text-xs font-normal text-subtle-foreground tabular-nums">
        {progressFigureOf(counts)}
      </span>
    </span>
  )
}
